import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supaFetch, db, getValidToken } from '../lib/supabase.js';
import { queryKeys } from '../lib/queryKeys.js';
import { SUPABASE_URL, SUPABASE_KEY } from '../lib/constants.js';

// Best-effort: fires notify-order-status edge function after a successful status update.
// Never throws — notification failure must never block the admin's UI.
async function fireStatusNotification(orderId, newStatus) {
  const notifiable = ['confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];
  if (!notifiable.includes(newStatus)) return;
  try {
    const token = await getValidToken();
    await fetch(`${SUPABASE_URL}/functions/v1/notify-order-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_KEY,
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ order_id: orderId, new_status: newStatus }),
    });
  } catch (e) {
    console.warn('[notify-order-status] non-fatal:', e.message);
  }
}

// ─── BUILD SUPABASE QUERY STRING ──────────────────────────────────────────────
// Status filter: 'all' → no filter, 'new' → captures both new + seen,
// any other value → exact match.
function buildOrdersQuery(status) {
  let q = '?order=created_at.desc';
  if (status === 'new') {
    q += '&status=in.(new,seen)';
  } else if (status !== 'all') {
    q += `&status=eq.${encodeURIComponent(status)}`;
  }
  return q;
}

// ─── useOrders ────────────────────────────────────────────────────────────────
// Fetches a paginated, server-filtered page of orders.
// Returns the full useQuery result: { data, isLoading, isError, isFetching, ... }
//   data.data       → order records for the current page
//   data.totalCount → total matching rows across all pages (from Content-Range)
export function useOrders({ page = 1, limit = 50, status = 'all' } = {}) {
  const from = (page - 1) * limit;
  const to   = from + limit - 1;

  return useQuery({
    queryKey: queryKeys.admin.orders({ page, limit, status }),
    queryFn: () =>
      supaFetch(
        'GET',
        'orders',
        null,
        buildOrdersQuery(status),
        {
          // Supabase Range header → offset pagination
          'Range': `${from}-${to}`,
          // Supabase requires count=exact alongside Range for the total
          'Prefer': 'count=exact,return=representation',
        }
      ),
    // Anti-flicker: keep showing previous page data while the next page loads
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    gcTime:    10 * 60 * 1000,
  });
}

// ─── useAllOrders ─────────────────────────────────────────────────────────────
// Fetches ALL orders without pagination — used for customer order-history panels.
// Only loads when `enabled` is true to avoid unnecessary fetches.
export function useAllOrders({ enabled = true } = {}) {
  return useQuery({
    queryKey: ['orders', 'all'],
    queryFn: () => supaFetch('GET', 'orders', null, '?order=created_at.desc'),
    select: (result) => result.data ?? [],
    enabled,
    staleTime: 60_000, // 1 min — history panel doesn't need real-time precision
  });
}

// ─── useUpdateOrderStatus ─────────────────────────────────────────────────────
// Optimistic mutation: instantly reflects the new status in the UI, rolls back
// on network error, and invalidates all orders queries on settle.
export function useUpdateOrderStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, newStatus }) =>
      db.update('orders', orderId, { status: newStatus }),

    onMutate: async ({ orderId, newStatus }) => {
      // Cancel any in-flight orders queries to avoid race conditions
      await queryClient.cancelQueries({ queryKey: queryKeys.admin.ordersRoot() });

      // Snapshot all currently-cached orders query entries for rollback
      const snapshot = queryClient.getQueriesData({
        queryKey: queryKeys.admin.ordersRoot(),
      });

      // Optimistically update every cached orders page
      queryClient.setQueriesData(
        { queryKey: queryKeys.admin.ordersRoot() },
        (old) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((o) =>
              o.id === orderId ? { ...o, status: newStatus } : o
            ),
          };
        }
      );

      return { snapshot };
    },

    onError: (_err, _vars, context) => {
      // Roll back all optimistic updates using the snapshot
      if (context?.snapshot) {
        context.snapshot.forEach(([key, value]) => {
          queryClient.setQueryData(key, value);
        });
      }
    },

    onSuccess: (_data, { orderId, newStatus }) => {
      // Fire customer notification after optimistic update is confirmed server-side
      fireStatusNotification(orderId, newStatus);
    },

    onSettled: () => {
      // Always resync with the server after mutation settles (success or error)
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.ordersRoot() });
    },
  });
}

// ─── useDeleteOrder ───────────────────────────────────────────────────────────
// Simple (non-optimistic) delete: removes the row, then invalidates.
export function useDeleteOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId }) => db.delete('orders', orderId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.ordersRoot() });
    },
  });
}

// ─── useSaveAdminNote ─────────────────────────────────────────────────────────
export function useSaveAdminNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orderId, note }) =>
      db.update('orders', orderId, { admin_notes: note }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.ordersRoot() });
    },
  });
}
